import { useMultipleUsers } from 'hooks/useMultipleUsers'
import { useProgramSubscriptions, useSubscriptions } from 'hooks/useSubscriptions'

import { ProgramType } from 'types/program'
import { UserType } from 'types/user'

/**
 * @param trainerId - The ID of the trainer to get the leaderboard of their Group for.
 * @returns The Group leaderboard filtered for only members of the Group for the trainer.
 */
export const useLeaderboard = ({
  trainerId,
  programId,
  type = ProgramType.GROUP,
}: {
  trainerId?: string
  programId?: string
  type?: ProgramType
}) => {
  const { data: groupSubscriptions, isLoading: isGroupSubscriptionsLoading } = useSubscriptions({
    trainerId,
  })
  const { data: programSubscriptions, isLoading: isProgramSubscriptionsLoading } =
    useProgramSubscriptions({ programId })

  const subscriptions = type === ProgramType.PROGRAM ? programSubscriptions : groupSubscriptions
  const isSubscriptionsLoading =
    type === ProgramType.PROGRAM ? isProgramSubscriptionsLoading : isGroupSubscriptionsLoading

  const userIds = subscriptions?.map((s) => s.user.id) ?? []
  const uniqueUserIds = userIds ? ([...new Set([trainerId, ...userIds])] as string[]) : []

  const { data: users, isLoading: isMultipleUsersLoading } = useMultipleUsers({
    userIds: uniqueUserIds,
  })

  const sortedUsers = users?.sort((a, b) => {
    if (!a?.upscore || !b?.upscore) return 0
    return b.upscore.total - a.upscore.total
  }) as UserType[]

  return {
    data: sortedUsers,
    isLoading: isSubscriptionsLoading || isMultipleUsersLoading,
  }
}
